import React from 'react';

import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { FormattedMessage, useIntl } from 'react-intl';
import { PartnerDetails } from 'apis/PartnerApi';
import { Alert } from 'react-bootstrap';

type MergeConfirmStepProps = {
    confirmed: boolean;
    error: string;
    loading: boolean;
    onConfirmChange: (confirmed: boolean) => void;
    sourcePartner: PartnerDetails;
    targetPartner: PartnerDetails;
};

export const MergeConfirmStep = (props: MergeConfirmStepProps) => {

    const { confirmed, error, loading, onConfirmChange, sourcePartner, targetPartner } = props;

    const { formatMessage } = useIntl();

    return (
        <div className="merge-confirm-step mt-3">
            {loading ? (
                <div className="text-center py-5">
                    <GMetrixLoader />
                    <p className="mt-3 text-muted">
                        <strong>
                            <FormattedMessage defaultMessage="Merging in progress..." id="merging_in_progress.c61b17cf" />
                        </strong>
                        <br />
                        <FormattedMessage defaultMessage="Please do not close this window." id="please_do_not_close_this_window.328b3779" />
                    </p>
                </div>
            ) : (
                <>
                    <div className="merge-review-columns">
                        <div className="merge-review-card merge-review-card--source">
                            <div className="merge-review-card__header merge-review-card__header--source">
                                <i className="fa-solid fa-building me-2" />
                                {<FormattedMessage defaultMessage="Account to Keep" id="account_to_keep.980b996d" /> || 'Source'}
                            </div>
                            <div className="merge-review-card__body">
                                <div className="merge-review-card__field">
                                    <span className="merge-review-card__label">
                                        {<FormattedMessage defaultMessage="Organization" description="New" id="organization.7cb537a9" /> || 'Organization'}
                                    </span>
                                    <span className="merge-review-card__value">
                                        {sourcePartner.Name}
                                    </span>
                                </div>
                                <div className="merge-review-card__field">
                                    <span className="merge-review-card__label">
                                        <FormattedMessage defaultMessage="ID" id="id.e9650a61" />
                                    </span>
                                    <span className="merge-review-card__value">
                                        {sourcePartner.ID}
                                    </span>
                                </div>
                                <div className="merge-review-card__field">
                                    <span className="merge-review-card__label">
                                        {<FormattedMessage defaultMessage="Email" id="email.500d9e86" /> || 'Email'}
                                    </span>
                                    <span className="merge-review-card__value">
                                        {sourcePartner.ContactEmail || '—'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="merge-review-card merge-review-card--target">
                            <div className="merge-review-card__header merge-review-card__header--target">
                                <i className="fa-solid fa-right-to-bracket me-2" />
                                {<FormattedMessage defaultMessage="Account to Merge" id="account_to_merge.20112764" /> || 'Merge'}
                            </div>
                            <div className="merge-review-card__body">
                                <div className="merge-review-card__field">
                                    <span className="merge-review-card__label">
                                        {<FormattedMessage defaultMessage="Organization" description="New" id="organization.7cb537a9" /> || 'Organization'}
                                    </span>
                                    <span className="merge-review-card__value">
                                        {targetPartner.Name}
                                    </span>
                                </div>
                                <div className="merge-review-card__field">
                                    <span className="merge-review-card__label">
                                        <FormattedMessage defaultMessage="ID" id="id.e9650a61" />
                                    </span>
                                    <span className="merge-review-card__value">
                                        {targetPartner.ID}
                                    </span>
                                </div>
                                <div className="merge-review-card__field">
                                    <span className="merge-review-card__label">
                                        {<FormattedMessage defaultMessage="Email" id="email.500d9e86" /> || 'Email'}
                                    </span>
                                    <span className="merge-review-card__value">
                                        {targetPartner.ContactEmail || '—'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Alert className="mt-3 d-flex align-items-start" role="alert" variant="danger">
                        <i className="fa-solid fa-ban me-2 mt-1" />
                        <div>
                            <strong className="d-block mb-2">
                                {<FormattedMessage defaultMessage="Warning" id="warning.64b35335" /> || 'Warning'}
                            </strong>
                            <ul className="mb-0 ps-3">
                                <li>
                                    <FormattedMessage
                                        values={{
                                            b: chunks => (
                                                <strong>
                                                    {chunks}
                                                </strong>
                                            ),
                                            sourceId: sourcePartner.ID,
                                            sourceName: sourcePartner.Name,
                                            targetId: targetPartner.ID,
                                            targetName: targetPartner.Name
                                        }} defaultMessage="<b>{targetName}</b> (ID: {targetId}) will be permanently merged into <b>{sourceName}</b> (ID: {sourceId})"
                                        id="targetname_id_targetid_will_be_permanent.4a7ca776"
                                    />
                                </li>
                                <li>
                                    <FormattedMessage defaultMessage="All orders, licenses, and users will be transferred to the source" id="all_orders_licenses_and_users_will_be_tr.9daa418e" />
                                </li>
                                <li>
                                    <FormattedMessage defaultMessage="The target Testing Center will be deactivated" id="the_target_testing_center_will_be_deacti.f2d1ae14" />
                                </li>
                            </ul>
                        </div>
                    </Alert>

                    {error && (
                        <Alert className="mt-3" role="alert" variant="danger">
                            <i className="fa-solid fa-circle-exclamation me-2" />
                            {error}
                        </Alert>
                    )}

                    <div className="mt-4">
                        <GMetrixCheckInput
                            label={formatMessage(
                                { id: 'i_understand_targetname_sourcename.da483c06', defaultMessage: 'I understand: "{targetName}" → "{sourceName}".' },
                                { sourceName: sourcePartner.Name, targetName: targetPartner.Name }
                            )}
                            checked={confirmed}
                            onChange={() => onConfirmChange(!confirmed)}
                            type="checkbox"
                        />
                    </div>
                </>
            )}
        </div>
    );

};
